import { useState, useMemo } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from '@tanstack/react-table'
import {
  FileEdit,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock,
  AlertCircle,
  XCircle,
  Ban,
  HelpCircle,
  CheckCircle,
  MessageCircle,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

interface Case {
  id: number
  token: string
  summary_title: string
  client_name: string
  state: 'draft' | 'submitted'
  state_display: string
  incident_type: string | null
  incident_date: string | null
  airline_code: string | null
  document_count: number
  unread_client_message_count: number
  created_at: string
  updated_at: string
  is_draft: boolean
}

interface CasesTableProps {
  cases: Case[]
}

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return 'Nie'

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSecs < 60) return 'Gerade eben'
  if (diffMins < 60) return `${diffMins} Min.`
  if (diffHours < 24) return `${diffHours} Std.`
  if (diffDays < 7) return `${diffDays} T.`

  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '—'

  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function getIncidentIcon(incidentType: string | null) {
  switch (incidentType) {
    case 'delay':
      return <Clock className="h-3.5 w-3.5" />
    case 'cancel':
      return <XCircle className="h-3.5 w-3.5" />
    case 'denied_boarding':
      return <Ban className="h-3.5 w-3.5" />
    case 'other':
      return <AlertCircle className="h-3.5 w-3.5" />
    default:
      return <HelpCircle className="h-3.5 w-3.5" />
  }
}

function getIncidentLabel(incidentType: string | null): string {
  switch (incidentType) {
    case 'delay':
      return 'Verspätung'
    case 'cancel':
      return 'Annullierung'
    case 'denied_boarding':
      return 'Nichtbeförderung'
    case 'other':
      return 'Sonstiges'
    default:
      return '—'
  }
}

const columnHelper = createColumnHelper<Case>()

export function CasesTable({ cases }: CasesTableProps) {
  const navigate = useNavigate()
  // Default sort: submitted cases first (is_draft ASC), then by updated_at DESC
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'state', desc: false },
    { id: 'updated_at', desc: true },
  ])

  const columns = useMemo(
    () => [
      columnHelper.accessor('token', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Fall
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => (
          <span className="font-mono font-bold text-slate-900 tracking-wide">
            {row.original.token}
          </span>
        ),
      }),
      columnHelper.accessor('summary_title', {
        header: 'Titel',
        cell: ({ getValue }) => (
          <span className="font-medium text-foreground truncate max-w-[280px] block">
            {getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('client_name', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Mandant
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="text-slate-700">{getValue()}</span>
        ),
      }),
      columnHelper.accessor('state', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Status
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => {
          const isDraft = row.original.is_draft
          const unreadCount = row.original.unread_client_message_count || 0

          return (
            <div className="flex items-center gap-3">
              {/* Status indicator */}
              {isDraft ? (
                <div className="flex items-center gap-1.5 text-slate-500">
                  <FileEdit className="h-4 w-4" />
                  <span className="text-sm font-medium">Entwurf</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-green-600">
                  <CheckCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">Eingereicht</span>
                </div>
              )}

              {/* Unread chat indicator */}
              {unreadCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                    <MessageCircle className="h-3 w-3" />
                    {unreadCount}
                  </span>
                </div>
              )}
            </div>
          )
        },
        sortingFn: (rowA, rowB) => {
          // Sort submitted (non-draft) cases first
          const aIsDraft = rowA.original.is_draft
          const bIsDraft = rowB.original.is_draft
          if (aIsDraft === bIsDraft) return 0
          return aIsDraft ? 1 : -1
        },
      }),
      columnHelper.accessor('incident_type', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Vorfall
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ row }) => {
          const incidentType = row.original.incident_type
          const airlineCode = row.original.airline_code
          return (
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 text-slate-600">
                {getIncidentIcon(incidentType)}
                {getIncidentLabel(incidentType)}
              </span>
              {airlineCode && (
                <Badge variant="outline" className="text-xs font-mono">
                  {airlineCode}
                </Badge>
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor('incident_date', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Datum
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="text-slate-600 text-sm">
            {formatDate(getValue() as string | null)}
          </span>
        ),
      }),
      columnHelper.accessor('updated_at', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Aktualisiert
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="text-slate-600 text-sm">
            {formatRelativeTime(getValue() as string | null)}
          </span>
        ),
      }),
      columnHelper.accessor('document_count', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-slate-900 transition-colors"
            onClick={() => column.toggleSorting()}
          >
            Dok.
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-3.5 w-3.5" />
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
            )}
          </button>
        ),
        cell: ({ getValue }) => (
          <span className="text-slate-600 tabular-nums">{getValue()}</span>
        ),
      }),
    ],
    []
  )

  const table = useReactTable({
    data: cases,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const handleRowClick = (caseItem: Case) => {
    navigate({ to: `/lawyer/cases/${caseItem.id}` })
  }

  return (
    <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent border-b-2">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="text-xs uppercase tracking-wider text-slate-500 font-semibold"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center text-slate-500">
                Keine Fälle gefunden.
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => {
              const isDraft = row.original.is_draft
              return (
                <TableRow
                  key={row.id}
                  onClick={() => handleRowClick(row.original)}
                  className={cn(
                    'group cursor-pointer transition-colors',
                    isDraft
                      ? 'bg-slate-50/50 hover:bg-slate-50'
                      : 'hover:bg-slate-50'
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
    </div>
  )
}
